/**
 * Shared helpers for the professional registration forms: preparing a profile photo for upload,
 * and turning an axios failure into something a human can read.
 *
 * Both exist because the five registration forms each hand-rolled the same two steps and got the
 * same two things wrong.
 */

/**
 * Longest edge, in pixels, of the image actually uploaded.
 *
 * There is NO size limit on what a professional may pick — a 40 MP phone photo is accepted. This
 * is a resolution ceiling applied after selection, not a validation rule that can reject a file.
 *
 * 1600 is chosen because the photo is only ever displayed as a profile avatar (largest render is a
 * few hundred CSS pixels), so anything beyond this is bytes nobody sees. A 6 MB phone photo comes
 * out around 300-500 KB, which is the difference between a form that submits in a second and one
 * that appears to hang for a minute on a mobile connection in Baghdad.
 */
const MAX_EDGE_PX = 1600;

/** JPEG quality for the re-encode. 0.9 is visually indistinguishable at avatar sizes. */
const JPEG_QUALITY = 0.9;

/**
 * Below this, re-encoding costs more than it saves and risks making a small PNG bigger, so the
 * original file is passed through untouched.
 */
const SKIP_BELOW_BYTES = 512 * 1024;

/** Accepted input types. Type is still checked — only the SIZE limit was removed. */
export const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

/**
 * Downscale and re-encode an image so the upload is small and fast.
 *
 * Deliberately never throws and never rejects a file. If anything fails — an unreadable file, a
 * browser without canvas, a decode error — the ORIGINAL file is returned and the upload proceeds.
 * A profile photo that uploads slowly is a far better outcome than a registration that cannot be
 * completed, which is what a throwing optimiser would produce.
 *
 * @param {File} file the file the professional selected or captured
 * @returns {Promise<File>} a smaller file, or the original when shrinking was not possible
 */
export async function prepareImageForUpload(file) {
  if (!file) return file;
  if (file.size <= SKIP_BELOW_BYTES) return file;

  try {
    const bitmap = await loadBitmap(file);
    const { width, height } = bitmap;
    if (!width || !height) return file;

    const scale = Math.min(1, MAX_EDGE_PX / Math.max(width, height));
    const targetW = Math.max(1, Math.round(width * scale));
    const targetH = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;

    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    // Better downscaling quality than the default nearest-neighbour-ish path.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);

    if (typeof bitmap.close === "function") bitmap.close();

    const blob = await canvasToBlob(canvas, "image/jpeg", JPEG_QUALITY);
    if (!blob) return file;

    // Keep the original when the re-encode did not actually help — true for an already-optimised
    // image, and for a small PNG that JPEG cannot beat.
    if (blob.size >= file.size) return file;

    return new File([blob], toJpegName(file.name), {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch {
    return file;
  }
}

/**
 * `createImageBitmap` where available (off-main-thread decode, so the UI does not jank on a large
 * photo), with an <img> fallback for Safari versions that lack it for File inputs.
 */
function loadBitmap(file) {
  if (typeof window !== "undefined" && typeof window.createImageBitmap === "function") {
    return window.createImageBitmap(file);
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    if (typeof canvas.toBlob !== "function") {
      resolve(null);
      return;
    }
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

function toJpegName(name) {
  const base = (name || "photo").replace(/\.[^./\\]+$/, "");
  return `${base || "photo"}.jpg`;
}

/**
 * Pull a displayable message out of an axios error.
 *
 * Every form previously called `toast.error(error)` with the Error OBJECT. react-toastify renders
 * a given node, and an Error is not a valid React child, so the toast came up empty or was
 * dropped entirely — which is why failures looked silent even though the catch block ran.
 *
 * Order matters: the API's own bilingual `message` is the most specific and is already translated,
 * so it wins. A request that never got a response is reported as connectivity rather than as a
 * server fault, because those need different actions from the user.
 *
 * @param {unknown} error the value caught
 * @param {string} fallback message to use when nothing better can be found
 * @returns {string} a non-empty, human-readable string
 */
export function getApiErrorMessage(error, fallback = "Something went wrong. Please try again.") {
  const data = error?.response?.data;

  if (data) {
    if (typeof data === "string" && data.trim()) return data.trim();
    if (typeof data.message === "string" && data.message.trim()) return data.message.trim();
    // Nest's ValidationPipe returns `message` as an array of field errors.
    if (Array.isArray(data.message) && data.message.length) return data.message.join(", ");
    if (typeof data.error === "string" && data.error.trim()) return data.error.trim();
  }

  // No response at all: DNS, offline, CORS, or a timeout.
  if (error?.code === "ECONNABORTED") {
    return "The upload timed out. Please check your connection and try again.";
  }
  if (error?.request && !error?.response) {
    return "Could not reach the server. Please check your internet connection and try again.";
  }

  const status = error?.response?.status;
  if (status) return `Request failed (${status}). Please try again.`;

  if (typeof error?.message === "string" && error.message.trim()) return error.message.trim();

  return fallback;
}
